// @ts-strict-ignore
import {
  Box,
  Tooltip,
  useSnackBar,
} from '@usealloy/component-library';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Position } from 'react-flow-renderer';

import {
  selectActionByPublicId,
  selectNodeState,
  selectWorkflowData,
  selectWorkflowProps,
  selectUnsetTagNodeIds,
  selectIsTagSwapMode,
  selectMergeDiff,
} from 'flow/store/redux/selectors';
import { setHoveredNodeId } from 'flow/store/redux/slice';

import type {
  NodeState,
  WorkflowNodeProps,
} from 'flow/service/graph';
import {
  COLUMN_SPACING,
  NODE_WIDTH,
  workflowNodeType,
} from 'flow/service/graph';

import { useDelayedState } from 'flow/editor/hooks';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';

import {
  StyledLeftHandle,
  StyledBaseNode,
  StyledRightHandle,
  StyledNodeText,
  StyledEvaluationSetTagNodeIndicator,
} from './BaseNode.styles';
import NodeConnector from './NodeConnector';
import {
  canDuplicateNode,
  doesActionSupportTooltip,
  doesNodeTypeSupportConnector,
  doesNodeTypeSupportMenu,
  getCanHoverOnNode,
  getDropdownItems,
  showNodeMenu,
} from './helpers';
import { formatColor } from '../../../helpers';
import NodeDropdownMenu from './NodeDropdownMenu';
import NodeTooltip from './NodeTooltip';
import { ActionType } from '~/dashboard/views/workflows/store/engine/constants';
import { useEditorAccess } from '../../../../context';
import ConfirmNodeDeletionModal from './ConfirmNodeDeletionModal';
import { isActionOfType } from 'flow/service/actions';
import { getIsEditable } from 'flow/service/general';
import { UserContext } from '~/dashboard/context';
import { updateCustomListVersionToActiveThunk } from 'flow/store/redux/thunks/services';
import UpdateToActiveListModal from '~/dashboard/views/settings/customLists/modals/UpdateCustomListToActiveVersion';
import { selectThresholdOutputResults } from 'flow/store/redux/selectors/workflowFeatures';
import { useCopyPasteHandlers } from 'flow/editor/components/SnippetCopyPaste/useCopyPasteHandlers';
import { isSnippetChildAction } from 'flow/service/actions/snippets';

type BaseNodeProps = {
  icon?: JSX.Element;
  label?: string | React.ReactNode;
  children?: JSX.Element;
  construction?: boolean;
  stateStyle: { baseBackgroundColor: string } & Partial<Record<NodeState, React.CSSProperties>>;
  menuTriggerStyle?: React.CSSProperties;
  isOutsideCanvas?: boolean;
  isChildNode?: boolean;
  style?: React.CSSProperties;
  disable?: {
    nodeConnector?: boolean;
    nodeMenu?: boolean;
    nodeMenuHoverState?: boolean;
  };
};

type SupportedNodeProps = Pick<WorkflowNodeProps, 'id' | 'type'>;

function BaseNode({
  icon = null,
  id,
  label,
  type,
  children,
  construction = false,
  stateStyle,
  menuTriggerStyle,
  isOutsideCanvas = false,
  isChildNode = false,
  disable,
  style,
}: BaseNodeProps & SupportedNodeProps) {
  const { featureFlags } = useContext(UserContext);
  const addSnackBar = useSnackBar();
  const { handleCopy } = useCopyPasteHandlers();

  const dispatch = useAppDispatch();
  const workflowProps = useAppSelector(selectWorkflowProps);
  const workflowData = useAppSelector(selectWorkflowData);
  const nodeState = useAppSelector(state => selectNodeState(state, id));
  // id might be null under _some_ sporadic edge cases
  const action = useAppSelector(selectActionByPublicId(id));
  const unsetTagNodeIds = useAppSelector(selectUnsetTagNodeIds);
  const isTagSwapMode = useAppSelector(selectIsTagSwapMode);
  const mergeDiff = useAppSelector(selectMergeDiff);
  const thresholdOutputResults = useAppSelector(selectThresholdOutputResults);

  const isTagNodeSet = useMemo(() => thresholdOutputResults
    && !(unsetTagNodeIds.includes(id)), [id, unsetTagNodeIds, thresholdOutputResults]);

  const [isDropdownTriggerHovered, setIsDropdownTriggerHovered] = useState<boolean>(false);
  const [isNodeConnectorHovered, setIsNodeConnectorHovered] = useState<boolean>(false);
  const [isNodeDropdownVisible, setIsNodeDropdownVisible] = useState<boolean>(false);
  const [isNodeBeingUpdated, setIsNodeBeingUpdated] = useState<boolean>(false);

  const isSnippetChild = isSnippetChildAction(action);
  const { editorMode } = useEditorAccess();
  const isEditable = getIsEditable(editorMode);

  const {
    reset: closeRuleTooltip, value: isRuleTooltipOpen, set: setRuleTooltipState,
  } = useDelayedState<boolean>({
    initialState: false,
    valueSetDelay: 750,
  });

  const [nodeDeletionChildActionCounts, setNodeDeletionChildActionCounts] = useState<number>(0);

  const resetNodeChildActionCounts = () => setNodeDeletionChildActionCounts(0);

  const handleUpdateCustomListVersion = useCallback(() => {
    dispatch(updateCustomListVersionToActiveThunk(id));
    setIsNodeBeingUpdated(false);
  }, [dispatch, id]);


  // Close tooltip immediately when entering:
  //  - tag swap mode
  //  - paste review mode where we see the merge diff compared on the graph
  //  - or the node in the process of being updated
  useEffect(() => {
    if (isTagSwapMode.isActive || mergeDiff || isNodeBeingUpdated) {
      closeRuleTooltip();
    }
  }, [isTagSwapMode.isActive, closeRuleTooltip, isNodeBeingUpdated, mergeDiff]);

  const canDuplicate = (
    Boolean(featureFlags.workflowDuplicateNode) && canDuplicateNode(type)
  );

  const dropdownItems = useMemo(() => getDropdownItems({
    addSnackBar,
    dispatch,
    nodeId: id,
    workflowData,
    workflowProps,
    setNodeDeletionChildActionCounts,
    nodeType: type,
    onUpdateAction: setIsNodeBeingUpdated,
    closeRuleTooltip,
    onCopy: featureFlags.copyPasteGA ? handleCopy : undefined,
    canDuplicate,
  }), [
    addSnackBar,
    dispatch,
    id,
    workflowData,
    workflowProps,
    type,
    closeRuleTooltip,
    featureFlags.copyPasteGA,
    canDuplicate,
    handleCopy,
  ]);

  const openRuleTooltip = useCallback(() => {
    const tooltipState = doesActionSupportTooltip(action)
      || (nodeState === 'hovered' || nodeState === 'focused-hovered')
      || isDropdownTriggerHovered
      || isNodeConnectorHovered
      || isNodeDropdownVisible;

    setRuleTooltipState(tooltipState);
  }, [
    action, isDropdownTriggerHovered, isNodeConnectorHovered,
    isNodeDropdownVisible, nodeState, setRuleTooltipState,
  ]);

  const handleMouseEnter = useCallback(() => {
    dispatch(setHoveredNodeId(id));

    // Don't open tooltip when in tag swap mode or node in the process of being updated
    if (!isTagSwapMode.isActive && !isNodeBeingUpdated) {
      openRuleTooltip();
    }
  }, [dispatch, id, openRuleTooltip, isTagSwapMode, isNodeBeingUpdated]);


  const handleMouseLeave = useCallback(() => {
    if (!isNodeDropdownVisible) {
      dispatch(setHoveredNodeId(null));
    }
    closeRuleTooltip();
  }, [dispatch, isNodeDropdownVisible, closeRuleTooltip]);

  const TooltipContent = useMemo<React.ReactNode>(
    /**
     * there is a quirk in the react-flow internals causing Node objects to be created
     * for an Action that has been immediately deleted. to be safe, we only want to render
     * the tooltip when there is an Action.
     */
    () => (action ? <NodeTooltip action={action} /> : null),
    [action],
  );

  const { baseBackgroundColor = 'var(--cl-gray-200)' } = stateStyle;

  const dataTestId = `workflowNode-${type}-${id}`;

  /**
   * construction nodes are invisible nodes that other nodes can connect to
   */
  if (construction && !isOutsideCanvas) {
    return (
      <StyledBaseNode
        baseBackgroundColor={baseBackgroundColor}
        nodeState="normal"
        data-testid={dataTestId}
        data-value={label}
      >
        <StyledLeftHandle type="target" position={Position.Left} nodeType={type} />
        <StyledRightHandle type="source" position={Position.Right} nodeType={type} />
      </StyledBaseNode>
    );
  }

  const canHover = getCanHoverOnNode(mergeDiff, id);

  return (
    <Tooltip
      align="start"
      hide={isActionOfType(action, ActionType.EXIT)}
      content={TooltipContent}
      delayDuration={750}
      disableHoverableContent
      doHideArrow
      open={isRuleTooltipOpen}
      side="bottom"
      style={{ maxWidth: `${(NODE_WIDTH * 2) + COLUMN_SPACING}px` }}
      variant="secondary"
    >
      <StyledBaseNode
        data-dd-privacy="allow"
        baseBackgroundColor={formatColor(baseBackgroundColor)}
        nodeState={nodeState}
        style={{
          ...style,
          ...stateStyle[nodeState],
        }}
        onMouseEnter={canHover ? handleMouseEnter : undefined}
        onMouseLeave={canHover ? handleMouseLeave : undefined}
        isOutsideCanvas={isOutsideCanvas}
        isChildNode={isChildNode}
        isFolderNode={type === workflowNodeType.folder}
        data-testid={dataTestId}
        data-value={label}
        data-dd-action-name={`workflow-node-${type}`}
      >
        {!isOutsideCanvas && (
          <StyledLeftHandle type="target" position={Position.Left} nodeType={type} />
        )}
        <Box gap={1} alignItems="center" justifyContent="flex-start">
          {icon}
          {typeof label === 'string' ? (
            <StyledNodeText
              isChildNode={isChildNode}
              isOutsideCanvas={isOutsideCanvas}
              nodeState={nodeState}
            >
              {label}
            </StyledNodeText>
          ) : (
            label
          )}
        </Box>

        {children}

        {isEditable
          && !isSnippetChild
          && !disable?.nodeMenu
          && (disable?.nodeMenuHoverState
            ? doesNodeTypeSupportMenu(type)
            : showNodeMenu(nodeState, type)
          )
          && (
            <NodeDropdownMenu
              data-dd-action-name={`workflow-node-${type}-dropdown`}
              backgroundColor={baseBackgroundColor}
              onDropdownVisibilityChange={isOpen => {
                if (!isOpen) { dispatch(setHoveredNodeId(null)); }
                setIsNodeDropdownVisible(isOpen);
              }}
              label={label}
              dropdownItems={dropdownItems}
              onTriggerMouseEnter={() => {
                setIsDropdownTriggerHovered(true);
                closeRuleTooltip();
              }}
              onTriggerMouseLeave={() => {
                setIsDropdownTriggerHovered(false);
                openRuleTooltip();
              }}
              menuTriggerStyle={menuTriggerStyle}
            />
          )}

        {!isEditable && isTagNodeSet && featureFlags?.ruleExplainabilityPhase >= 4 && (
          <StyledEvaluationSetTagNodeIndicator />
        )}

        {nodeDeletionChildActionCounts > 0 && (
          <ConfirmNodeDeletionModal
            nodeDeletionChildActionCounts={nodeDeletionChildActionCounts}
            resetNodeChildActionCounts={resetNodeChildActionCounts}
            id={id}
          />
        )}

        {isNodeBeingUpdated && (
          <UpdateToActiveListModal
            isOpen={isNodeBeingUpdated}
            onClose={() => setIsNodeBeingUpdated(false)}
            onUpdate={handleUpdateCustomListVersion}
            nodeId={id}
          />
        )}

        {!isOutsideCanvas && (
          <StyledRightHandle type="source" position={Position.Right} nodeType={type} />
        )}

        {isEditable
          && !disable?.nodeConnector
          && doesNodeTypeSupportConnector(type)
          && (nodeState === 'hovered' || nodeState === 'focused-hovered')
          && (
            <NodeConnector
              connectorParentNodeId={id}
              onMouseEnter={() => {
                setIsNodeConnectorHovered(true);
                closeRuleTooltip();
              }}
              onMouseLeave={() => {
                setIsNodeConnectorHovered(false);
                openRuleTooltip();
              }}
            />
          )}
      </StyledBaseNode>
    </Tooltip>
  );
}


export default React.memo(BaseNode);
